import { z } from 'zod';
/**
 * Report query spec. The server compiles it to SQL against a virtual flattened
 * view (core columns + joined display columns + custom fields). Agents never
 * write SQL.
 */
export const REPORT_ENTITIES = [
    'customers',
    'quotes',
    'jobs',
    'appointments',
    'invoices',
    'invoice_lines',
    'payments',
    'expenses',
    'bank_transactions',
    'services',
    'staff',
    'credit_notes',
    'vendors',
    'documents',
    'proposals',
];
export const FILTER_OPS = [
    'eq',
    'neq',
    'gt',
    'gte',
    'lt',
    'lte',
    'in',
    'not_in',
    'contains',
    'starts_with',
    'is_set',
    'is_empty',
    'between',
    'within_days',
];
export const RELATIVE_RANGES = [
    'today',
    'yesterday',
    'this_week',
    'last_week',
    'this_month',
    'last_month',
    'this_quarter',
    'this_year',
    'last_year',
    'last_7_days',
    'last_30_days',
    'last_90_days',
    'last_365_days',
];
export const ReportFilter = z.object({
    field: z.string().min(1),
    op: z.enum(FILTER_OPS),
    value: z.unknown().optional(),
});
export const ReportAggregate = z.object({
    fn: z.enum(['count', 'sum', 'avg', 'min', 'max', 'count_distinct']),
    field: z.string().optional(),
    as: z.string().regex(/^[a-z][a-z0-9_]{0,40}$/),
});
export const ReportSpec = z.object({
    entity: z.enum(REPORT_ENTITIES),
    columns: z.array(z.string()).optional(),
    filters: z.array(ReportFilter).default([]),
    date_range: z
        .object({
        field: z.string().optional(), // defaults per entity (issued_at, incurred_at, posted_at, starts_at, created_at)
        relative: z.enum(RELATIVE_RANGES).optional(),
        from: z.string().optional(), // ISO date
        to: z.string().optional(),
    })
        .optional(),
    group_by: z.array(z.string()).optional(),
    aggregates: z.array(ReportAggregate).optional(),
    sort: z.array(z.object({ field: z.string(), dir: z.enum(['asc', 'desc']).default('asc') })).optional(),
    limit: z.number().int().min(1).max(1000).default(100),
    offset: z.number().int().min(0).default(0),
});
export const SavedReportInput = z.object({
    key: z.string().regex(/^[a-z][a-z0-9_]{0,60}$/).optional(),
    name: z.string().min(1).max(120),
    description: z.string().max(500).optional(),
    spec: ReportSpec,
});
export const REPORT_GUIDE = `
REPORT SPEC
  entity:     customers | quotes | jobs | appointments | invoices | invoice_lines | payments | expenses | bank_transactions | services | staff | credit_notes | vendors | documents | proposals
  columns:    core columns of the entity, "fields.<custom_key>", and joined display columns:
              customer.display_name customer.email customer.fields.<key>  job.number job.title  invoice.number
              vendor.name  bank_account.name  service.name  service.category  service_location.label
  filters:    [{"field":"status","op":"in","value":["sent","overdue"]}]  ops: eq neq gt gte lt lte in not_in contains starts_with is_set is_empty between within_days
  date_range: {"relative":"this_month"} or {"from":"2026-01-01","to":"2026-03-31"} (field defaults to the entity's main date)
  group_by:   ["customer.display_name"]  with aggregates: [{"fn":"sum","field":"balance","as":"outstanding"},{"fn":"count","as":"invoices"}]
  currency:   a report that totals money is split by currency automatically, and a "currency" column appears in the output.
              Amounts in different currencies are never added together, because no exchange rates are held.
  sort:       [{"field":"outstanding","dir":"desc"}]   limit: up to 1000
Examples
  Outstanding AR by customer:  {"entity":"invoices","filters":[{"field":"status","op":"in","value":["sent","partially_paid","overdue"]}],
                                "group_by":["customer.display_name"],"aggregates":[{"fn":"sum","field":"balance","as":"outstanding"},{"fn":"count","as":"invoices"}],
                                "sort":[{"field":"outstanding","dir":"desc"}]}
  Revenue by service this month: {"entity":"invoice_lines","date_range":{"relative":"this_month"},"filters":[{"field":"invoice.status","op":"neq","value":"void"}],
                                "group_by":["service.name"],"aggregates":[{"fn":"sum","field":"line_total","as":"revenue"}]}
`.trim();
//# sourceMappingURL=reports.js.map